import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import {
  getUserLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  setPrimaryLocation,
  type CreateLocationInput,
  type UpdateLocationInput,
} from '../services/locations';

// Infer location type from the service return type
type LocationData = Awaited<ReturnType<typeof getUserLocations>>[number];

/**
 * React Query hook for user locations
 *
 * Features:
 * - Automatic fetching of user locations
 * - Optimistic updates for all mutations
 * - Primary location management
 * - Individual loading states for each mutation
 *
 * Usage:
 *   const { locations, primaryLocation, createLocation, deleteLocation } = useLocations();
 *   createLocation.mutate({ name: 'Home', latitude: 40.7, longitude: -74.0 });
 */

export function useLocations() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Query key for this user's locations
  const queryKey = ['locations', user?.id];

  // Fetch all locations
  const {
    data: locations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return getUserLocations(user.id);
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });

  // Get primary location from the list
  const primaryLocation = locations.find(loc => loc.isPrimary) || null;

  // Create location mutation
  const createMutation = useMutation({
    mutationFn: (input: Omit<CreateLocationInput, 'userId'>) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return createLocation({ ...input, userId: user.id });
    },
    onMutate: async (newLocation) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousLocations = queryClient.getQueryData(queryKey);

      // Optimistically add new location
      queryClient.setQueryData(queryKey, (old: LocationData[] = []) => {
        const isFirstLocation = old.length === 0;
        const optimisticLocation = {
          id: `temp-${Date.now()}`,
          ...newLocation,
          userId: user?.id,
          isPrimary: isFirstLocation,
          createdAt: new Date().toISOString(),
        };
        return [optimisticLocation, ...old];
      });

      return { previousLocations };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
      // Failed to create location (suppressed)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update location mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateLocationInput }) => {
      return updateLocation(id, updates);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousLocations = queryClient.getQueryData(queryKey);

      // Optimistically update location
      queryClient.setQueryData(queryKey, (old: LocationData[] = []) => {
        return old.map(loc =>
          loc.id === id ? { ...loc, ...updates } : loc
        );
      });

      return { previousLocations };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
      // Failed to update location (suppressed)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete location mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return deleteLocation(id, user.id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previousLocations = queryClient.getQueryData(queryKey);

      // Optimistically remove location
      queryClient.setQueryData(queryKey, (old: LocationData[] = []) => {
        const filtered = old.filter(loc => loc.id !== id);

        // If we deleted the primary location, set another as primary
        const deletedLocation = old.find(loc => loc.id === id);
        if (deletedLocation?.isPrimary && filtered.length > 0) {
          filtered[0] = { ...filtered[0], isPrimary: true };
        }

        return filtered;
      });

      return { previousLocations };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
      // Failed to delete location (suppressed)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Set primary location mutation
  const setPrimaryMutation = useMutation({
    mutationFn: (locationId: string) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return setPrimaryLocation(locationId, user.id);
    },
    onMutate: async (locationId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousLocations = queryClient.getQueryData(queryKey);

      // Optimistically update primary status
      queryClient.setQueryData(queryKey, (old: LocationData[] = []) => {
        return old.map(loc => ({
          ...loc,
          isPrimary: loc.id === locationId,
        }));
      });

      return { previousLocations };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
      // Failed to set primary location (suppressed)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    // Data
    locations,
    primaryLocation,
    hasLocations: locations.length > 0,
    locationCount: locations.length,

    // Loading states
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingPrimary: setPrimaryMutation.isPending,

    // Error states
    error,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    setPrimaryError: setPrimaryMutation.error,

    // Mutations
    createLocation: createMutation.mutate,
    updateLocation: updateMutation.mutate,
    deleteLocation: deleteMutation.mutate,
    setPrimaryLocation: setPrimaryMutation.mutate,

    // Async versions for promise handling
    createLocationAsync: createMutation.mutateAsync,
    updateLocationAsync: updateMutation.mutateAsync,
    deleteLocationAsync: deleteMutation.mutateAsync,
    setPrimaryLocationAsync: setPrimaryMutation.mutateAsync,

    // Utilities
    refetch,
    getLocationById: (id: string) => locations.find(loc => loc.id === id),
  };
}

/**
 * Hook for a single location by ID
 * Useful for location detail pages
 *
 * Usage:
 *   const { location, isLoading } = useLocation(locationId);
 */
export function useLocation(locationId: string | undefined) {
  const { locations, isLoading, error } = useLocations();

  const location = locationId
    ? locations.find(loc => loc.id === locationId)
    : null;

  return {
    location,
    isLoading,
    error,
    notFound: !isLoading && locationId && !location,
  };
}

/**
 * Hook specifically for the primary location
 * Returns null if no primary location is set
 *
 * Usage:
 *   const { primaryLocation, isLoading } = usePrimaryLocation();
 */
export function usePrimaryLocation() {
  const { primaryLocation, isLoading, error } = useLocations();

  return {
    primaryLocation,
    isLoading,
    error,
    hasPrimaryLocation: !!primaryLocation,
  };
}
